import {ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import { BreadcrumbModel } from '../../../models/misc/breadcrumb.model';
import { Const, ResourceType } from '../../../helpers/Const';
import { NavigationService } from '../../../../service/services/misc/navigation/navigation.service';
import { firstValueFrom, Subscription, Observable } from 'rxjs';
import { ResourceContentComponent } from '../../../../features/organizations/misc/resource-content/resource-content.component';
import { TablePersonalizationDefinitions } from '../../../helpers/TablePersonalizationDefinitions';
import { CreateGenericResourceDialogComponent } from '../../../../features/organizations/dialogs/create-generic-resource-dialog/create-generic-resource-dialog.component';
import { ResourceModel } from '../../../models/resources/resource.model';
import { MenuComponent } from '@fundamental-ngx/core';
import { DialogService } from '../../../../service/services/misc/dialog/dialog.service';
import { CreateInstanceDialogComponent } from '../../../../features/organizations/dialogs/create-instance-dialog/create-instance-dialog.component';
import {AvailableInstancesModel, RTDModel} from '../../../models/resources/rtd.model';
import { ResourceDataService } from '../../../../service/services/rest/resource-data/resource-data.service';
import { HeaderTitleService } from '../../../../service/services/misc/header-title/header-title.service';
import {EventService} from "../../../../service/services/misc/event/event.service";
import {UisService} from "../../../../service/services/misc/uis/uis.service";
import { OrganizationHierarchyStoreService } from '../../../../service/store/organization-hierarchy-store/organization-hierarchy-store.service';
import {SwaService} from "../../../../service/services/misc/swa/swa.service";
import {
    MoveResourceStatueDialogComponent
} from "../../../../features/organizations/dialogs/move-resource-status-dialog/move-resource-status-dialog.component";
import {ResourceNotificationService} from "../../../../service/services/misc/fcl/resource-notification.service";
import {Utils} from "../../../helpers/Utils";
import {SavedFilterViewsService} from "../../../../service/services/misc/saved-filter-views/saved-filter-views.service";
import {SavedFilterView} from "../../../../service/services/misc/saved-filter-views/saved-filter-views.service";

@Component({
    template: '',
    standalone: false
})
export class ResourcePageComponent implements OnDestroy, OnInit {
    resourceType: ResourceType;
    tableDefinitions = TablePersonalizationDefinitions;
    currentResource: string | null;
    currentResourceDisplayName: string | null;
    currentOrg: string | null;
    searchTerm: string;
    noDataMessage: string;
    noDataFilteredMessage: string;
    subscriptions: Subscription[] = [];
    _fullPath: string;
    _currentPath: string;
    _currentProject: string;
    isFclOpen = false;
    loading: boolean;
    systemResources: RTDModel[] | null = [];
    systemResourcesByGroup: any[];
    serviceInstances: RTDModel[] | null = [];
    applicationResources: any[] = [];
    allServices: AvailableInstancesModel;
    parentPath: string;
    error: Error;
    movingOperationID: string;
    availableResourceTypes: string[] = [];
    advancedSearchReady = false;
    advancedSearchOpen = false;
    advancedSearchTerms: any;
    currentFilterViewName = 'Standard';

    RESOURCE_MOVING_IN_PROCESS = Const.RESOURCE_MOVING_IN_PROCESS;

    constructor(
        protected _organizationHierarchyStore: OrganizationHierarchyStoreService,
        protected _navigationService: NavigationService,
        protected _dialogService: DialogService,
        protected _headerTitleService: HeaderTitleService,
        protected _resourceDataService: ResourceDataService,
        protected cdRef: ChangeDetectorRef,
        protected eventService: EventService,
        protected uisService: UisService,
        protected swaService: SwaService,
        protected resourceNotificationService: ResourceNotificationService,
        protected savedFilterViewsService: SavedFilterViewsService
    ) {}

    @ViewChild('resourceContent')
        resourceContent: any; // Can be ResourceContentComponent or OrganizationContentComponent

    @ViewChild('menu')
        menu: MenuComponent;

    doSearch(searchTerm: any) {
        this.searchTerm = searchTerm;
    }

    ngOnInit(): void {
        this.subscriptions.push(this.eventService.pageLoadEventListener().subscribe(msg =>{
            if(msg === 'success') {
                this.cdRef.detectChanges();
            }
        }));

        this.subscriptions.push(
            this.resourceNotificationService.getResourceCreatedObservable().subscribe(
                ({ parentResource, createdResource }) => {
                    // Check if this is for current path
                    const fullPath = '/' + this._fullPath;
                    if (parentResource.path === fullPath && createdResource.path === fullPath) {
                        this.resourceContent.createResource(createdResource);
                    }
                }
            )
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    async loadServices() {
        const path = this._fullPath;
        const type = this.resourceType;
        this.loading = true;

        try {
            this.allServices = await firstValueFrom(this._resourceDataService.getAvailableServices(`${type}/${path}`));
            this.systemResourcesByGroup = (this.allServices.systemResources || []).filter((resource: any) => {
                return (
                    resource.type !== ResourceType.SERVICE_INSTANCE /*&& resource.type !== ResourceType.MANAGED_SERVICE*/
                );
            });
            this.applicationResources = this.allServices.applicationResources;

        } finally {
            // this.errorMsg = e.message;
            this.loading = false;
        }
    }

    async loadDiscoveryApiResources() {
        const path = this._fullPath ? '/' + this._fullPath : '';
        this.loading = true;
        this.advancedSearchReady = false;

        try {
            const response = await firstValueFrom(
                this._resourceDataService.getDiscoveryApiResources(path)
            );
            this.availableResourceTypes = response || [];
            this.advancedSearchReady = true;
        } catch (error) {
            console.error('Failed to load discovery API resources:', error);
            this.availableResourceTypes = [];
            this.advancedSearchReady = false;
        } finally {
            this.loading = false;
        }
    }

    setParentPath(path: string) {
        if (this.resourceType === ResourceType.ORGANIZATION) {
            this.parentPath = '/';
        } else {
            const pathMatch = path.match('(.*)/.*');
            this.parentPath = "/" + pathMatch[1];
        }
    }
    sortFn(a: RTDModel, b: RTDModel) {
        if (a.type < b.type) {
            return -1;
        }
        if (a.type > b.type) {
            return 1;
        }
        return 0;
    }

    serviceHasSchema(service: any) {
        return service.versions?.length > 0 && service.versions[0].schema?.jsonSchema?.properties;
    }

    onCreateGenericResource() {
        if (this.menu) {
            this.menu.close();
        }

        let fullPath = '/' + this._fullPath;
        let dialogRef = this._dialogService.openSideDialog(
            CreateGenericResourceDialogComponent,
            {
                path: fullPath,
            },
            '62.5rem'
        );

        dialogRef.afterClosed.subscribe({
            next: (res: ResourceModel) => {
                if (res.path === fullPath) {
                    this.resourceContent.createResource(res);
                }
            },
        });
    }

    onCreateServiceInstance(resource?: any) {
        if (this.menu) {
            this.menu.close();
        }
        let fullPath = '/' + this._fullPath;

        const resourceTypeForUIS = resource ? resource.type : ResourceType.SERVICE_INSTANCE;

        const dialogRef = this._dialogService.openSideDialog(
            CreateInstanceDialogComponent,
            {
                path: fullPath,
                type: this.resourceType,
                selectedResource: resource,
                serviceInstancesByGroup: resource || undefined,
            },
            '62.5rem'
        );

        dialogRef.afterClosed.subscribe({
            next: (res) => {
                this.resourceContent.createResource(res);
            },
        });
    }

    reset() {
        if(this.resourceContent?.dataTable) {
            this.resourceContent.dataTable.reset();
        }
    }

    onAdvancedSearchToggled(isOpen: boolean): void {
        this.advancedSearchOpen = isOpen;

        if (isOpen) {
            const defaultView = this.savedFilterViewsService.getDefaultView();
            if (defaultView) {
                this.currentFilterViewName = defaultView.name;
                this.advancedSearchTerms = defaultView.filters;
            } else {
                this.currentFilterViewName = 'Standard';
                this.advancedSearchTerms = null;
                this.reset();
            }
        }
    }

    toggleFcl(open: boolean) {
        this.isFclOpen = open;
    }

    disableClearFilter() {
        return (
            !this.resourceContent ||
            !this.resourceContent.dataTable ||
            (this.resourceContent.dataTable.filterItems.length === 0 &&
                this.resourceContent.dataTable.sortProperty == this.resourceContent.dataTable.initialSortProperty &&
                this.resourceContent.dataTable.ascending === true)
        );
    }

    async setResourceTitles() {
        let organization;
        try {
            organization = await this._organizationHierarchyStore.getOrFetchOrganization(this.currentOrg);
        } catch {
            organization = new ResourceModel({ name: this.currentOrg, displayName: this.currentOrg });
        }
        const breadcrumbs = await this.generatePageTitle(this._currentPath || "/", organization, this._currentProject || "");
        this._headerTitleService.setTitle(breadcrumbs);

        if (this.resourceType === ResourceType.ORGANIZATION) {
            this.currentResourceDisplayName = organization.displayName;
        } else {
            try {
                const resource = await this._organizationHierarchyStore
                    .getOrFetchContainer(organization.name, this.parentPath, this.currentResource, this.resourceType);
                this.currentResourceDisplayName = resource.displayName;
            } catch {
                this.currentResourceDisplayName = this.currentResource;
            }
        }
    }

    async generatePageTitle(path: string, org: ResourceModel, proj: string): Promise<BreadcrumbModel[]> {
        const baseUrl = this._navigationService.getNavigationUrl([
            "project",
            proj || Const.ALL_PROJECTS_PARAM,
            "organizations"
        ]);
        const orgUrl = this._navigationService.getNavigationUrl([
            "project",
            proj || Const.ALL_PROJECTS_PARAM,
            "organizations",
            org.name
        ]);
        const initialBreadcrumb: BreadcrumbModel = {
            text: $localize`:XTIT|Organization drilldown page title@@organizations.content.title:Organizations`,
            name: "",
            index: 0,
            url: "/app#" + baseUrl,
            type: null,
            path: "",
            loading: false,
            siblings: undefined,
            isOpen: false,
            totalCount: 0,
            continueToken: ""
        };

        const orgBreadcrumb: BreadcrumbModel = {
            text: org.displayName,
            name: org.name,
            index: 0,
            url: path === "/" ? "" : "/app#" + orgUrl,
            type: ResourceType.ORGANIZATION,
            path: "",
            loading: false,
            siblings: undefined,
            isOpen: false,
            totalCount: 0,
            continueToken: ""
        };
        let breadcrumbs: BreadcrumbModel[] = [orgBreadcrumb];

        const resourcePaths = Utils.mapPathToResourcePaths(org.name, path);

        const promises = this._organizationHierarchyStore.fetchContainersByPaths(this.resourceType, org.name, resourcePaths);
        let allResults = await Promise.all(promises);

        const nestedBreadcrumbs = this.mapResourcesToBreadcrumbs(proj, org.name, allResults);
        breadcrumbs = breadcrumbs.concat(nestedBreadcrumbs);

        breadcrumbs.unshift(initialBreadcrumb);
        return breadcrumbs;
    }

    mapPathToResourcePaths(orgName: string, path: string): { name: string, path: string }[] {
        let split = path.split("/").filter(Boolean);

        let initialPath = orgName;

        return split.map(item => {
            const name = item;
            const path = initialPath;
            initialPath = `${initialPath}/${item}`;
            return { name, path };
        });
    }

    mapResourcesToBreadcrumbs(projectName: string, orgName: string, resources: ResourceModel[]) {
        return resources.map((item, index, arr) => {
            const isLast = index === arr.length - 1;
            let url = isLast ? "" : "/app#" +
                this._navigationService.getNavigationUrl(
                    ["project", projectName || Const.ALL_PROJECTS_PARAM, "organizations", orgName || "", "folders"],
                    { queryParams: { path: `${item.path.slice(orgName.length + 1)}/${item.name}` } }
                );
            const path = item.path?.charAt(0) === "/" ? item.path?.substring(1) : item.path;
            let breadcrumb: BreadcrumbModel = {
                text: item?.displayName || item.name,
                name: item.name,
                url: url,
                index: index,
                path: path,
                loading: false,
                type: !isLast ? ResourceType.FOLDER : this.resourceType,
                siblings: undefined,
                isOpen: false,
                totalCount: 0,
                continueToken: ""
            };
            return breadcrumb;
        });
    }

    handleMoving(operationID: string) {
        this.movingOperationID = operationID;
    }

    async openOperationJson() {
        try {
            const resourceStatus = await firstValueFrom(this._resourceDataService.getMoveStatus(this.movingOperationID));
            const data = {
                operationId: this.movingOperationID,
                status: resourceStatus.status
            };
            this._dialogService.openDialog(MoveResourceStatueDialogComponent, data);
        } catch (e) {
            console.log("Could not get move resource status", e);
        }
    }

    onSavedViewSelected(savedView: SavedFilterView) {
        this.currentFilterViewName = savedView.name;

        // If selecting "Standard" view, clear filters but keep advanced search open
        if (savedView.name === 'Standard') {
            // Force a change by passing null explicitly, then trigger reset
            // This ensures ngOnChanges fires even if already null
            this.advancedSearchTerms = { __reset: true };
            setTimeout(() => {
                this.advancedSearchTerms = null;
                // Clear the results table
                if (this.resourceContent) {
                    this.resourceContent.onClearContent();
                }
            }, 0);
            return;
        }

        // Open advanced search if not already open
        if (!this.advancedSearchOpen) {
            this.advancedSearchOpen = true;
        }

        // Pass the saved filters to be loaded
        this.advancedSearchTerms = savedView.filters;
    }

    getOrganizationPath(): string {
        // For organizations component, use _currentProject
        // For folder and resourcegroup components, use currentOrg + _currentPath
        if (this.resourceType === ResourceType.ORGANIZATION) {
            return this._currentProject || '';
        }
        return `${this.currentOrg}${this._currentPath || ''}`;
    }

    onViewSaved(viewName: string): void {
        this.currentFilterViewName = viewName;
    }

    onFiltersChanged(): void {
        // When filters change, add a * to indicate unsaved changes
        // Only add * if we're not already on "Standard" view and the name doesn't already have *
        if (this.currentFilterViewName &&
            this.currentFilterViewName !== 'Standard' &&
            !this.currentFilterViewName.endsWith('*')) {
            this.currentFilterViewName = this.currentFilterViewName + '*';
        }
    }
}
